import { DateTime } from 'luxon';
import assert from '../../core/util/assert';
import notEmpty from '../../core/util/notEmpty';
import type {
    FutureStockQuantities,
    GeneralNextAvailabilityStatus,
    ProductOrderRestriction,
    StockQuantity,
} from '../types';
import { availabilityOrder, StockStatuses } from '../types';

const sortByAvailability = (a: string, b: string) => {
    return availabilityOrder.indexOf(a) - availabilityOrder.indexOf(b);
};

const isAvailableNow = (quantity: number, desiredQuantity: number) => {
    return desiredQuantity <= quantity;
};

const getNextAvailabilityStatus = ({
    nextAvailability,
    desiredQuantity,
    totalAvailableQuantity,
    currentStockOnly = false,
}: {
    nextAvailability: GeneralNextAvailabilityStatus;
    desiredQuantity: number;
    totalAvailableQuantity: number;
    currentStockOnly?: boolean;
}) => {
    const notInStockStatus = {
        status: StockStatuses.NOT_IN_STOCK,
        quantity: desiredQuantity - totalAvailableQuantity,
    };

    switch (nextAvailability) {
        case 'noLongerAvailable': {
            return {
                status: StockStatuses.NO_LONGER_AVAILABLE,
            };
        }
        case 'onDemand':
            return currentStockOnly
                ? notInStockStatus
                : {
                      status: StockStatuses.ON_DEMAND,
                      quantity: desiredQuantity - totalAvailableQuantity,
                  };
        case 'unknown':
            return currentStockOnly
                ? notInStockStatus
                : {
                      status: StockStatuses.UNKNOWN,
                      quantity: desiredQuantity - totalAvailableQuantity,
                  };
        default:
            throw new Error('unknown nextAvailability status encountered');
    }
};

const isAvailableFromFutureStock = (
    totalAvailableQuantity: number,
    desiredQuantity: number,
    futureStock?: FutureStockQuantities
) => {
    return (
        totalAvailableQuantity >= desiredQuantity ||
        futureStock?.quantities.some(item => !item.quantity)
    );
};

const getStatusesFromFutureStock = (
    quantity: number,
    desiredQuantity: number,
    futureStock?: FutureStockQuantities
) => {
    if (!futureStock) {
        return {
            futureStockStatuses: [],
            totalAvailableQuantity: quantity,
        };
    }

    let totalAvailableQuantity = quantity;

    const futureStockStatuses: {
        status: StockStatuses;
        quantity?: number;
        date?: string;
    }[] = [];

    for (const futureStockItem of futureStock.quantities) {
        futureStockStatuses.push({
            status: StockStatuses.AVAILABLE_IN_FUTURE,
            quantity: futureStockItem.quantity,
            date: DateTime.fromISO(futureStockItem.date).toFormat('dd.LL.yyyy'),
        });

        totalAvailableQuantity += futureStockItem.quantity ?? 0;

        if (totalAvailableQuantity >= desiredQuantity) {
            break;
        }
    }

    return {
        futureStockStatuses,
        totalAvailableQuantity,
    };
};

const getPendingStatuses = (
    store: string
): {
    status: StockStatuses;
    quantity?: number;
    date?: string;
}[] => {
    return [
        {
            status: ['ic'].includes(store)
                ? StockStatuses.NO_LONGER_AVAILABLE
                : StockStatuses.CANNOT_BE_ORDERED_IN_DESIRED_BRANCH,
        },
    ];
};

const getStockStatus = ({
    store,
    stock,
    futureStock,
    restriction,
    desiredQuantity = 1,
    currentStockOnly = false,
}: {
    store: string;
    stock?: StockQuantity;
    restriction?: ProductOrderRestriction;
    futureStock?: FutureStockQuantities;
    desiredQuantity?: number;
    currentStockOnly?: boolean;
}): {
    status: StockStatuses;
    quantity?: number;
    date?: string;
    restrictionId?: string;
}[] => {
    if (
        !stock ||
        stock.unavailable ||
        !notEmpty(stock.quantity) ||
        !notEmpty(stock.nextAvailability)
    ) {
        return getPendingStatuses(store);
    }

    if (restriction?.restrictionId) {
        return [
            {
                status: StockStatuses.RESTRICTED_FOR_ORDER,
                restrictionId: restriction.restrictionId,
            },
        ];
    }

    const { quantity, nextAvailability } = stock;
    assert(notEmpty(quantity), 'stock.quantity cannot be empty.');
    assert(nextAvailability, 'nextAvailability is required.');

    const getStatuses = (): {
        status: StockStatuses;
        quantity?: number;
        date?: string;
    }[] => {
        const statuses: {
            status: StockStatuses;
            quantity?: number;
            date?: string;
            restrictionId?: string;
        }[] = [];

        if (quantity) {
            statuses.push({
                status: StockStatuses.AVAILABLE,
                quantity,
            });

            if (isAvailableNow(quantity, desiredQuantity)) {
                return statuses;
            }
        }

        if (currentStockOnly) {
            return [
                ...statuses,
                getNextAvailabilityStatus({
                    nextAvailability,
                    desiredQuantity,
                    totalAvailableQuantity: quantity,
                    currentStockOnly,
                }),
            ];
        }

        const { futureStockStatuses, totalAvailableQuantity } =
            getStatusesFromFutureStock(quantity, desiredQuantity, futureStock);

        statuses.push(...futureStockStatuses);

        if (futureStock?.quantities.length) {
            if (
                isAvailableFromFutureStock(
                    totalAvailableQuantity,
                    desiredQuantity,
                    futureStock
                )
            ) {
                return statuses;
            }
        }

        return [
            ...statuses,
            getNextAvailabilityStatus({
                nextAvailability,
                desiredQuantity,
                totalAvailableQuantity,
            }),
        ];
    };

    return getStatuses().sort((a, b) => sortByAvailability(a.status, b.status));
};

export default getStockStatus;
